'use client';

import * as React from 'react';
import { MapPinIcon } from 'lucide-react';

import { Button } from '@workspace/ui/components/button';
import { Card, CardContent } from '@workspace/ui/components/card';
import { Input } from '@workspace/ui/components/input';
import { Label } from '@workspace/ui/components/label';
import { toast } from '@workspace/ui/components/sonner';
import { Textarea } from '@workspace/ui/components/textarea';

import { GridSection } from '~/components/fragments/grid-section';
import { SiteHeading } from '~/components/fragments/site-heading';
import { useMarketingAnalytics } from '~/hooks/use-marketing-analytics';
import {
  hasBrowserWeb3Forms,
  submitBrowserWeb3FormsLead
} from '~/lib/web3forms-client';

export function Contact(): React.JSX.Element {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);
  const { trackContactSubmit, trackContactSubmitFailed } =
    useMarketingAnalytics();

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const data = {
      firstName: formData.get('firstName') as string,
      lastName: formData.get('lastName') as string,
      email: formData.get('email') as string,
      company: (formData.get('company') as string) || undefined,
      role: (formData.get('role') as string) || undefined,
      botcheck: (formData.get('botcheck') as string) || undefined,
      message: formData.get('message') as string
    };

    try {
      if (data.botcheck) {
        toast.success("Message sent! We'll be in touch soon.");
        formRef.current?.reset();
        return;
      }

      if (hasBrowserWeb3Forms()) {
        await submitBrowserWeb3FormsLead({
          formName: 'Viventium contact form',
          subject: `Viventium contact form: ${data.firstName} ${data.lastName}`,
          fields: {
            firstName: data.firstName,
            lastName: data.lastName,
            name: `${data.firstName} ${data.lastName}`,
            email: data.email,
            company: data.company,
            role: data.role,
            message: data.message,
            source_url: 'https://www.viventium.ai/contact'
          }
        });
      } else {
        const response = await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        });

        const result = (await response.json().catch(() => ({}))) as {
          success?: string | boolean;
        };

        if (
          !response.ok ||
          result.success === false ||
          result.success === 'false'
        ) {
          throw new Error('Failed to send');
        }
      }

      toast.success("Message sent! We'll be in touch soon.");
      trackContactSubmit('contact_page_form');
      formRef.current?.reset();
    } catch {
      trackContactSubmitFailed('contact_page_form');
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <GridSection>
      <div className="container space-y-20 py-20">
        <SiteHeading
          badge="Contact"
          title={
            <>
              Talk to a human
              <br /> when you need one
            </>
          }
        />
        <div className="lg:container lg:max-w-6xl">
          <div className="flex flex-col justify-between gap-10 lg:flex-row lg:gap-20">
            <div className="order-2 space-y-8 text-center lg:order-1 lg:w-1/2 lg:text-left">
              <h3 className="hidden max-w-fit text-4xl font-semibold lg:block">
                Contact
              </h3>
              <p className="text-muted-foreground lg:max-w-[80%]">
                Use this page when GitHub and the docs are not enough. Ask a
                question, raise a blocker, or reach out about collaboration.
              </p>
              <div className="space-y-4">
                <h4 className="hidden text-lg font-medium lg:block">
                  {/* Contact details */}
                </h4>
                <div className="flex flex-col items-center gap-3 lg:items-start">
                  {/* <ContactInfo
                    icon={MailIcon}
                    text="helloviventium@gmail.com"
                  /> */}
                  <ContactInfo
                    icon={MapPinIcon}
                    text="San Francisco, California"
                  />
                </div>
              </div>
            </div>
            <Card className="order-1 mx-auto w-full py-6 lg:py-10 max-w-lg shadow-lg lg:order-2 lg:w-1/2">
              <CardContent className="px-6 lg:px-10">
                <form
                  ref={formRef}
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-6"
                >
                  <input
                    type="text"
                    name="botcheck"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 grid w-full items-center gap-1.5 sm:col-span-1">
                      <Label htmlFor="firstName">First Name</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        type="text"
                        placeholder="John"
                        required
                      />
                    </div>
                    <div className="col-span-2 grid w-full items-center gap-1.5 sm:col-span-1">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        type="text"
                        placeholder="Doe"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid w-full items-center gap-1.5">
                    <Label htmlFor="email">Work Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@company.com"
                      required
                    />
                  </div>
                  <div className="grid w-full items-center gap-1.5">
                    <Label htmlFor="company">Company (optional)</Label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      placeholder="Acme Inc."
                    />
                  </div>
                  <div className="grid w-full items-center gap-1.5">
                    <Label htmlFor="role">Role (optional)</Label>
                    <Input
                      id="role"
                      name="role"
                      type="text"
                      placeholder="Founder, researcher, engineer..."
                    />
                  </div>
                  <div className="grid w-full gap-1.5">
                    <Label htmlFor="message">How can we help?</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Tell us what you need..."
                      rows={5}
                      required
                      minLength={10}
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send message'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </GridSection>
  );
}

type ContactInfoProps = {
  icon: React.ElementType;
  text: string;
};

function ContactInfo({
  icon: Icon,
  text
}: ContactInfoProps): React.JSX.Element {
  return (
    <div className="flex items-center gap-2 text-sm lg:w-64">
      <Icon className="size-4 shrink-0 text-muted-foreground" />
      <span>{text}</span>
    </div>
  );
}
